import { Injectable } from '@angular/core';

export type CarrierCode =
	| 'FEDEX'
	| 'UPS'
	| 'USPS'
	| 'DHL'
	| 'ONTRAC'
	| 'AMAZON'
	| 'LASERSHIP'
	| 'CANADAPOST'
	| null;

const CARRIER_TEMPLATES: Record<NonNullable<CarrierCode>, string> = {
	FEDEX: 'https://www.fedex.com/fedextrack/?trknbr=%s',
	UPS: 'https://www.ups.com/track?tracknum=%s',
	USPS: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=%s',
	DHL: 'https://www.dhl.com/us-en/home/tracking.html?tracking-id=%s',
	ONTRAC: 'https://www.ontrac.com/tracking?number=%s',
	AMAZON: 'https://track.amazon.com/tracking/%s',
	LASERSHIP: 'https://www.lasership.com/track.php?track_number_input=%s',
	CANADAPOST: 'https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor=%s',
};

@Injectable({
	providedIn: 'root',
})
export class CarrierTrackingService {
	detectCarrier(trackingNumber?: string | null, shipperName?: string | null): CarrierCode {
		const fromName = this.detectFromShipperName(shipperName);
		if (fromName) {
			return fromName;
		}
		return this.detectFromTrackingNumber(trackingNumber);
	}

	getCarrierLabel(code: CarrierCode): string {
		if (!code) {
			return '';
		}
		const labels: Record<NonNullable<CarrierCode>, string> = {
			FEDEX: 'FedEx',
			UPS: 'UPS',
			USPS: 'USPS',
			DHL: 'DHL',
			ONTRAC: 'OnTrac',
			AMAZON: 'Amazon',
			LASERSHIP: 'LaserShip',
			CANADAPOST: 'Canada Post',
		};
		return labels[code] || '';
	}

	getTrackingColor(shipper: string | null | undefined): string {
		const code = this.detectFromShipperName(shipper);
		switch (code) {
		case 'FEDEX':
			return 'FedEx';
		case 'UPS':
			return 'UPS';
		case 'USPS':
			return 'USPS';
		case 'DHL':
			return 'DHL';
		default:
			return 'Default';
		}
	}

	getTrackingUrl(
		trackingNumber?: string | null,
		shipperName?: string | null,
		override?: string | null,
		shipperTemplateUrl?: string | null,
	): string | null {
		const raw = (trackingNumber ?? '').trim();
		if (!raw) {
			return null;
		}

		const fromOverride = this.resolveOverride(raw, override);
		if (fromOverride) {
			return fromOverride;
		}

		const fromShipperTemplate = this.resolveOverride(raw, shipperTemplateUrl);
		if (fromShipperTemplate) {
			return fromShipperTemplate;
		}

		const code = this.detectCarrier(raw, shipperName);
		if (!code || !CARRIER_TEMPLATES[code]) {
			return null;
		}
		return CARRIER_TEMPLATES[code].replace('%s', encodeURIComponent(raw));
	}

	private resolveOverride(raw: string, override?: string | null): string | null {
		if (override == null) {
			return null;
		}
		const o = String(override).trim();
		if (!o) {
			return null;
		}
		if (o.includes('%s')) {
			return o.split('%s').join(encodeURIComponent(raw));
		}
		if (/^https?:\/\//i.test(o)) {
			return o;
		}
		return null;
	}

	private normalizeShipperToken(name: string): string {
		return (name || '')
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, ' ')
			.replace(/\s+/g, ' ')
			.trim();
	}

	private detectFromShipperName(shipperName?: string | null): CarrierCode {
		const n = this.normalizeShipperToken(shipperName || '');
		if (!n) {
			return null;
		}
		if (/\bups\b/.test(n) || n.includes('united parcel')) {
			return 'UPS';
		}
		if (/\bfedex\b/.test(n) || n.includes('federal express')) {
			return 'FEDEX';
		}
		if (/\busps\b/.test(n) || n.includes('postal service') || n.includes('united states postal')) {
			return 'USPS';
		}
		if (/\bdhl\b/.test(n)) {
			return 'DHL';
		}
		if (/\bontrac\b/.test(n) || n.includes('on trac')) {
			return 'ONTRAC';
		}
		if (/\bamazon\b/.test(n) || n.includes('amzn')) {
			return 'AMAZON';
		}
		if (/\blasership\b/.test(n) || n.includes('laser ship')) {
			return 'LASERSHIP';
		}
		if (n.includes('canada post') || n.includes('canadapost')) {
			return 'CANADAPOST';
		}
		return null;
	}

	private detectFromTrackingNumber(trackingNumber?: string | null): CarrierCode {
		const raw = (trackingNumber ?? '').trim();
		if (!raw) {
			return null;
		}
		const compact = raw.replace(/\s+/g, '').toUpperCase();
		const alnum = compact.replace(/[^A-Z0-9]/g, '');

		if (/^1Z[0-9A-Z]{16}$/i.test(alnum)) {
			return 'UPS';
		}
		if (/^T[0-9]{10}$/i.test(alnum)) {
			return 'UPS';
		}
		if (/^K[0-9]{9}$/i.test(alnum)) {
			return 'UPS';
		}
		if (/^[0-9]{9}$/.test(alnum) && !/^000/.test(alnum)) {
			return 'UPS';
		}

		if (/^[0-9]{12}$/.test(alnum)) {
			if (/^(92|93|94|95|82|42|71)/.test(alnum)) {
				return 'USPS';
			}
			return 'FEDEX';
		}
		if (/^[0-9]{15}$/.test(alnum)) {
			if (/^(92|93|94|95|82|42|71)/.test(alnum)) {
				return 'USPS';
			}
			return 'FEDEX';
		}
		if (/^[0-9]{20}$/.test(alnum)) {
			if (/^(92|93|94|95|82|42|71)/.test(alnum)) {
				return 'USPS';
			}
			return 'FEDEX';
		}
		if (/^96[0-9]{10,20}$/.test(alnum) && alnum.length >= 20) {
			return 'FEDEX';
		}

		if (/^(JD|JJD)/i.test(alnum)) {
			return 'DHL';
		}
		if (/^[0-9]{10}$/.test(alnum)) {
			return 'DHL';
		}

		if (/^(C|D)[0-9]{14}$/i.test(alnum)) {
			return 'ONTRAC';
		}

		if (/^(1LS|LS|LX|LJ)[A-Z0-9]+$/i.test(alnum)) {
			return 'LASERSHIP';
		}

		if (/^TBA[0-9]{12,}$/i.test(alnum) || /^[0-9]{12}-[0-9]{7}-[0-9]{7}$/.test(raw)) {
			return 'AMAZON';
		}

		if (/^(94|92|93|95|82|42|71)[0-9]{18,}$/.test(alnum) && alnum.length >= 20) {
			return 'USPS';
		}
		if (/^[A-Z]{2}[0-9]{9}US$/i.test(alnum)) {
			return 'USPS';
		}

		if (/^[0-9]{13,16}$/.test(alnum) && alnum.startsWith('420')) {
			return 'USPS';
		}

		return null;
	}
}
